import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../../App';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import ThemeSettingsPanel from '../ui/ThemeSettingsPanel';
import PumpProfileDialog from '../PumpProfileDialog';
import { 
  LayoutDashboard, 
  Gauge, 
  Users, 
  Receipt, 
  BarChart3, 
  Bell, 
  Settings, 
  UserCircle,
  LogOut,
  Menu,
  X,
  Fuel,
  Building2,
  AlertTriangle,
  FileText,
  Building,
  Zap,
  CreditCard,
  Ban,
  Sun,
  Moon,
  HelpCircle,
  BatteryCharging,
  Droplets
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import UserGuide from '../../pages/UserGuide';

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showThemeSettings, setShowThemeSettings] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [showPumpProfile, setShowPumpProfile] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [accessSettings, setAccessSettings] = useState(null);
  const [pumpProfile, setPumpProfile] = useState(null);
  const [brandTheme, setBrandTheme] = useState(null);
  const { user, logout, token } = useContext(AuthContext);
  const { 
    isDarkMode, 
    glassTransparency,
    glassBlur,
    backgroundColor,
    dealershipTheme, 
    updateDealership, 
    customColors 
  } = useTheme();

  // Load brand theme from localStorage and listen for changes
  useEffect(() => {
    const loadBrandTheme = () => {
      const savedBrandTheme = localStorage.getItem('dealershipBrandTheme');
      if (savedBrandTheme && savedBrandTheme !== 'none') {
        // Get the brand theme colors from CSS variables
        const primary = getComputedStyle(document.documentElement).getPropertyValue('--brand-primary').trim();
        const accent = getComputedStyle(document.documentElement).getPropertyValue('--brand-accent').trim();
        const text = getComputedStyle(document.documentElement).getPropertyValue('--brand-text').trim();
        if (primary) {
          setBrandTheme({ id: savedBrandTheme, primary, accent, text });
        }
      } else {
        setBrandTheme(null);
      }
    };
    
    // Initial load
    loadBrandTheme();
    
    // Listen for brand theme change custom event (fired from Settings page)
    const handleBrandThemeChanged = (event) => {
      const { themeId, theme } = event.detail;
      if (themeId && themeId !== 'none' && theme) {
        setBrandTheme({ id: themeId, primary: theme.primary, accent: theme.accent, text: theme.text });
      } else {
        setBrandTheme(null);
      }
    };
    
    // Listen for storage changes (for cross-tab sync)
    const handleStorageChange = () => {
      loadBrandTheme();
    };
    
    window.addEventListener('brandThemeChanged', handleBrandThemeChanged);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('brandThemeChanged', handleBrandThemeChanged);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch notifications, subscription status, pump profile, and access settings
  useEffect(() => {
    if (token) {
      fetchNotifications();
      fetchSubscriptionStatus();
      fetchAccessSettings();
      fetchPumpProfile();

      
      // Poll for new notifications every 60 seconds
      const interval = setInterval(() => {
        fetchNotifications();
      }, 60000);
      
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchPumpProfile = async () => {
    try {
      const res = await axios.get(`${API}/pump/profile`);
      setPumpProfile(res.data);
      // Update dealership theme based on pump profile
      if (res.data?.dealership) {
        updateDealership(res.data.dealership);
      }
    } catch (error) {
      console.error('Failed to fetch pump profile');
    }
  };

  const fetchAccessSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setAccessSettings({
        page_access: res.data.page_access || null,
        report_access: res.data.report_access || null
      });
    } catch (error) {
      console.error('Failed to fetch access settings');
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=10`);
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(res.data);
      
      // Show browser notification for expiring subscription
      if (res.data.is_expiring_soon && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification('Fuelogic - Subscription Expiring', {
            body: `Your subscription expires in ${res.data.days_remaining} days. Renew now!`,
            icon: '/favicon.ico'
          });
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission();
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscription status');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  // Page ID mapping from route to settings key
  const routeToPageId = {
    '/': 'dashboard',
    '/daily-operations': 'daily_operations',
    '/quick-actions': 'quick_actions',
    '/accounts': 'accounts',
    '/incidents': 'incidents',
    '/reports': 'reports',
    '/audit-logs': 'audit_logs',
    '/alerts': 'alerts',
    '/customers': 'customers',
    '/payments': 'payments',
    '/settings': 'settings',
    '/pump-profile': 'pump_profile',
    '/users': 'user_management',
    '/multi-station': 'multi_station',
    '/e-charging': 'e_charging',
    '/lubricants': 'lubricants'
  };

  // Pages allowed for suspended accounts
  const suspendedAllowedPages = ['dashboard', 'daily_operations', 'quick_actions', 'payments'];
  
  // Check if current page is restricted for suspended accounts
  const currentPageId = routeToPageId[location.pathname];
  const isSuspended = subscriptionStatus?.is_suspended;
  const isPageRestricted = isSuspended && currentPageId && !suspendedAllowedPages.includes(currentPageId);

  const allMenuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', pageId: 'dashboard' },
    { path: '/daily-operations', icon: Gauge, label: 'Daily Operations', pageId: 'daily_operations' },
    { path: '/quick-actions', icon: Zap, label: 'Quick Actions', pageId: 'quick_actions' },
    { path: '/e-charging', icon: BatteryCharging, label: 'E-Charging', pageId: 'e_charging' },
    { path: '/lubricants', icon: Droplets, label: 'Lubricants', pageId: 'lubricants' },
    { path: '/accounts', icon: Receipt, label: 'Accounts', pageId: 'accounts' },
    { path: '/reports', icon: BarChart3, label: 'Reports', pageId: 'reports' },
    { path: '/audit-logs', icon: FileText, label: 'Audit Logs', pageId: 'audit_logs' },
    { path: '/alerts', icon: Bell, label: 'Alerts', pageId: 'alerts' },
    { path: '/payments', icon: CreditCard, label: 'Payments', pageId: 'payments' },
    { path: '/settings', icon: Settings, label: 'Settings', pageId: 'settings' },
    { path: '/multi-station', icon: Building, label: 'Multi-Station', pageId: 'multi_station' }
  ];

  // Filter menu items based on user role and access settings
  const menuItems = allMenuItems.filter(item => {
    const userRole = user?.role || 'viewer';
    
    // If no access settings loaded yet, show default based on role
    if (!accessSettings?.page_access) {
      // Default access rules (handle both 'user' and 'standard' for backward compatibility)
      const defaultAccess = {
        admin: ['dashboard', 'daily_operations', 'quick_actions', 'accounts', 'incidents', 'reports', 'audit_logs', 'alerts', 'customers', 'payments', 'settings', 'pump_profile', 'user_management', 'multi_station', 'e_charging', 'lubricants'],
        manager: ['dashboard', 'daily_operations', 'quick_actions', 'accounts', 'incidents', 'reports', 'audit_logs', 'alerts', 'payments', 'e_charging', 'lubricants'],
        standard: ['dashboard', 'daily_operations', 'quick_actions', 'e_charging', 'lubricants'],
        viewer: ['dashboard']
      };
      return (defaultAccess[userRole] || []).includes(item.pageId);
    }
    
    // Use configured access settings
    const allowedPages = accessSettings.page_access[userRole] || [];
    return allowedPages.includes(item.pageId);
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Calculate glass effect values
  const getGlassOpacity = () => {
    return 0.95 - (glassTransparency / 100) * 0.80;
  };
  
  const getGlassBlurPx = () => {
    return Math.round((glassBlur / 100) * 40);
  };

  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div 
      className={`flex min-h-screen ${isDarkMode ? 'dark-mode-bg' : ''}`} 
      style={{ 
        backgroundColor: isDarkMode ? backgroundColor : (customColors?.background || '#F8FAFC')
      }}
    >
      <aside 
        className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 h-screen transform transition-transform duration-200 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isDarkMode ? 'glass-sidebar' : ''}`}
        style={{ 
          background: brandTheme 
            ? brandTheme.primary
            : (isDarkMode 
              ? hexToRgba(backgroundColor, getGlassOpacity() + 0.05)
              : (customColors?.sidebar || dealershipTheme.sidebarBg)),
          backdropFilter: isDarkMode && !brandTheme ? `blur(${getGlassBlurPx()}px)` : 'none',
          WebkitBackdropFilter: isDarkMode && !brandTheme ? `blur(${getGlassBlurPx()}px)` : 'none'
        }}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section - Compact */}
          <div className="p-4 border-b border-white/20">
            <div className="flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_f33e49fe-e820-4c95-aab4-40ccc87efe18/artifacts/dtk4fxv6_Fuelogic%20White%20Background%20Logo%20dark%20greay.jpg" 
                alt="Logo" 
                className="w-24 h-24 object-contain rounded-xl" 
              />
            </div>
          </div>

          {/* Navigation */}
          <nav 
            className="flex-1 px-3 py-4 space-y-1 overflow-y-auto sidebar-scrollbar"
            style={{
              '--scrollbar-track': brandTheme 
                ? brandTheme.primary
                : (isDarkMode 
                  ? hexToRgba(backgroundColor, getGlassOpacity() + 0.05)
                  : (customColors?.sidebar || dealershipTheme.sidebarBg))
            }}
          >
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                    isActive 
                      ? 'bg-white shadow-sm font-semibold' 
                      : 'text-white/90 hover:bg-white/20 hover:text-white'
                  }`}
                  style={isActive ? { color: brandTheme?.primary || customColors?.accent || dealershipTheme.primary } : {}}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon 
                    className={`w-5 h-5 ${isActive ? '' : 'text-white/80'}`} 
                    style={isActive ? { color: brandTheme?.primary || customColors?.accent || dealershipTheme.primary } : {}}
                    strokeWidth={isActive ? 2 : 1.5} 
                  />
                  <span className="text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-h-screen">
        {/* Combined Header with Pump Name and Actions */}
        <header 
          className={`text-white px-4 py-3 lg:px-8 ${isDarkMode ? 'glass-header' : ''}`}
          style={{ 
            background: brandTheme 
              ? brandTheme.primary
              : (isDarkMode 
                ? hexToRgba(backgroundColor, getGlassOpacity())
                : (customColors?.header || dealershipTheme.headerBg)),
            backdropFilter: isDarkMode && !brandTheme ? `blur(${getGlassBlurPx()}px)` : 'none',
            WebkitBackdropFilter: isDarkMode && !brandTheme ? `blur(${getGlassBlurPx()}px)` : 'none'
          }}
        >
          <div className="flex items-center justify-between w-full">
            {/* Left: Mobile menu + Station Name */}
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 -ml-2 text-white/80 hover:text-white"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              
              {pumpProfile && (
                <>
                  <div 
                    className="p-2 rounded-xl shadow-lg"
                    style={{
                      background: brandTheme 
                        ? brandTheme.accent 
                        : 'linear-gradient(to bottom right, #f97316, #d97706)'
                    }}
                  >
                    <Fuel className="w-6 h-6" style={{ color: brandTheme ? (brandTheme.id === 'bpcl' || brandTheme.id === 'shell' || brandTheme.id === 'jiobp' ? '#000' : '#fff') : '#fff' }} />
                  </div>
                  <button
                    onClick={() => setShowPumpProfile(true)}
                    className="text-left hover:opacity-80 transition-opacity cursor-pointer"
                    data-testid="pump-name-button"
                    title="Click to view Pump Profile"
                  >
                    <h1 className="font-heading font-bold text-xl lg:text-2xl tracking-tight text-white drop-shadow-sm">
                      {pumpProfile.pump_name}
                    </h1>
                    {pumpProfile.dealership && (
                      <span 
                        className="text-xs font-semibold uppercase tracking-wider"
                        style={{ 
                          color: brandTheme ? brandTheme.accent : (dealershipTheme.badgeText || '#fff'),
                          opacity: brandTheme ? 1 : 0.8
                        }}
                      >
                        {pumpProfile.dealership} Partner
                      </span>
                    )}
                  </button>
                </>
              )}
            </div>
            
            {/* Right: Icons and Logout */}
            <div className="flex items-center gap-2 lg:gap-4">
              {/* Help Button */}
              <Sheet open={showUserGuide} onOpenChange={setShowUserGuide}>
                <SheetTrigger asChild>
                  <button
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    data-testid="help-button"
                    title="User Guide"
                  >
                    <HelpCircle className="w-5 h-5 text-white/90" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:w-[540px] p-0">
                  <UserGuide onClose={() => setShowUserGuide(false)} />
                </SheetContent>
              </Sheet>

              {/* Theme Settings Button */}
              <button
                className="flex items-center gap-1 p-2 rounded-lg hover:bg-white/10 transition-colors"
                onClick={() => setShowThemeSettings(!showThemeSettings)}
                data-testid="theme-settings-toggle"
                title="Theme Settings"
              >
                {isDarkMode ? (
                  <Moon className="w-5 h-5 text-indigo-300" />
                ) : (
                  <Sun className="w-5 h-5 text-amber-300" />
                )}
                <Settings className="w-3 h-3 text-white/60" />
              </button>
              
              {/* Notification Bell */}
              <div className="relative">
                <button
                  className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
                  onClick={() => setShowNotifications(!showNotifications)}
                  data-testid="notification-bell"
                >
                  <Bell className="w-5 h-5 text-white/90" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50 text-foreground" data-testid="notification-dropdown">
                    <div className="p-3 border-b border-border flex justify-between items-center">
                      <span className="font-semibold">Notifications</span>
                      {unreadCount > 0 && (
                        <button 
                        onClick={markAllAsRead}
                        className="text-xs text-primary hover:underline"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notif) => (
                        <div 
                          key={notif.id} 
                          className={`p-3 border-b border-border hover:bg-secondary cursor-pointer ${!notif.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                          onClick={() => {
                            markAsRead(notif.id);
                            if (notif.action_url) navigate(notif.action_url);
                          }}
                        >
                          <div className="flex items-start gap-2">
                            {notif.is_critical && (
                              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${notif.is_critical ? 'text-red-600' : ''}`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-muted mt-1 line-clamp-2">{notif.message}</p>
                              <p className="text-xs text-muted mt-1">
                                {new Date(notif.created_at).toLocaleString('en-IN')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-muted text-sm">
                        No notifications
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t border-border">
                    <button 
                      onClick={() => {
                        navigate('/alerts');
                        setShowNotifications(false);
                      }}
                      className="w-full text-center text-sm text-primary hover:underline"
                    >
                      View all alerts
                    </button>
                  </div>
                </div>
              )}
            </div>

              {/* Logout Button with User Info */}
              <div className="flex flex-col items-end">
                <button
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
                <p className="text-xs text-white/60 mt-0.5 pr-2" data-testid="logged-in-user">
                  {user?.name} <span className="capitalize">({user?.role})</span>
                </p>
              </div>
            </div>
          </div>
          
          {/* Suspended Account Warning Banner */}
          {isSuspended && (
            <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Ban className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-800 dark:text-red-200">
                    Your account is suspended due to unpaid subscription. Limited access mode is active.
                  </span>
                </div>
                <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => navigate('/payments')}>
                  Pay Now
                </Button>
              </div>
            </div>
          )}
          
          {/* Subscription Warning Banner */}
          {!isSuspended && subscriptionStatus?.is_expiring_soon && (
            <div className="mt-3 p-3 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-md flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Your subscription expires in {subscriptionStatus.days_remaining} days
                </span>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate('/payments')}>
                Renew Now
              </Button>
            </div>
          )}
          
          {!isSuspended && subscriptionStatus?.is_expired && (
            <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-sm font-medium text-red-800 dark:text-red-200">
                  Your subscription has expired! Access may be limited.
                </span>
              </div>
              <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => navigate('/payments')}>
                Renew Now
              </Button>
            </div>
          )}
        </header>

        <main className="flex-1 p-4 lg:p-8 bg-background">
          {/* Show restriction overlay for suspended accounts on restricted pages */}
          {isPageRestricted ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
              <div className="p-8 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 max-w-md shadow-sm">
                <Ban className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-heading font-bold text-red-700 dark:text-red-400 mb-2">
                  Access Restricted
                </h2>
                <p className="text-red-600 dark:text-red-300 mb-6">
                  This page is not available while your account is suspended. 
                  Please renew your subscription to restore full access.
                </p>
                <Button 
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => navigate('/payments')}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Go to Payments
                </Button>
              </div>
            </div>
          ) : (
            <div className="page-container">
              {children}
            </div>
          )}
        </main>
      </div>
      
      {/* Click outside to close notifications */}
      {showNotifications && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowNotifications(false)}
        />
      )}
      
      {/* Theme Settings Panel */}
      <ThemeSettingsPanel 
        isOpen={showThemeSettings} 
        onClose={() => setShowThemeSettings(false)} 
      />
      
      {/* Pump Profile Dialog */}
      <PumpProfileDialog 
        open={showPumpProfile} 
        onOpenChange={setShowPumpProfile} 
      />
    </div>
  );
};

export default DashboardLayout;